import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Coins } from "lucide-react";

/**
 * Centered "First match of the day! +10 coins" celebration overlay.
 * Shown once per day on the player's first matched pair. Auto-dismisses
 * after ~2.4s with a smooth fade+slide animation.
 *
 * Props:
 *   open  — boolean, true to display
 *   coins — number, coins awarded (default 10)
 *   onDone — callback when the overlay fully dismisses
 */
export default function FirstMatchCelebration({ open, coins = 10, onDone }) {
  // Capture latest onDone in a ref so the timer doesn't restart every render
  // (the parent's inline `() => setX(0)` recreates on each render, which was
  // making the useEffect cleanup fire continuously and the card never
  // actually dismissed — user reported being unable to play because the
  // celebration overlay stayed forever).
  const onDoneRef = React.useRef(onDone);
  React.useEffect(() => { onDoneRef.current = onDone; }, [onDone]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => onDoneRef.current?.(), 2400);
    return () => clearTimeout(t);
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <div
          data-testid="first-match-celebration"
          className="fixed inset-0 z-[200] pointer-events-none flex items-center justify-center px-4"
          aria-live="polite"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.85 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 320, damping: 22 }}
            className="relative w-full max-w-[340px] rounded-3xl overflow-hidden shadow-2xl"
            style={{
              background: "linear-gradient(135deg,#FCD9A0 0%,#E8A365 55%,#B97A2F 100%)",
              boxShadow: "0 20px 60px rgba(184,121,47,0.55), inset 0 2px 0 rgba(255,255,255,0.45)",
            }}
          >
            {/* Soft pulse aura */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0.6 }}
              animate={{ scale: 1.04, opacity: 0.9 }}
              transition={{ duration: 1.2, repeat: 1, repeatType: "reverse" }}
              className="absolute inset-0 rounded-3xl"
              style={{
                background: "radial-gradient(ellipse at top, rgba(255,255,255,0.55), transparent 65%)",
              }}
            />
            {/* Drifting sparkles */}
            {[0, 1, 2, 3, 4].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 0, y: 0, scale: 0.6 }}
                animate={{
                  opacity: [0, 1, 0],
                  x: (i % 2 === 0 ? -1 : 1) * (30 + i * 14),
                  y: -20 - i * 12,
                  scale: 1,
                  rotate: 25 * (i - 2),
                }}
                transition={{ duration: 1.6, delay: 0.1 + i * 0.12 }}
                className="absolute top-6 left-1/2 -translate-x-1/2 text-white"
              >
                <Sparkles size={18} strokeWidth={2.2} />
              </motion.div>
            ))}

            <div className="relative px-6 py-5 flex items-center gap-4">
              <motion.div
                initial={{ rotate: -25, scale: 0.6 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 14, delay: 0.05 }}
                className="shrink-0 w-16 h-16 rounded-full grid place-items-center"
                style={{
                  background: "radial-gradient(circle at 35% 30%, #FFF4C9 0%, #F6BA6B 60%, #B97A2F 100%)",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.25), inset 0 -3px 6px rgba(0,0,0,0.18), inset 0 2px 4px rgba(255,255,255,0.6)",
                  border: "3px solid #FCD9A0",
                }}
              >
                <Coins size={28} strokeWidth={2} color="#4D2A12" />
              </motion.div>
              <div className="min-w-0">
                <p
                  className="text-[11px] uppercase tracking-[0.18em] font-bold"
                  style={{ color: "#4D2A12", opacity: 0.78 }}
                >
                  Daily bonus
                </p>
                <p
                  style={{
                    fontFamily: "Outfit, sans-serif",
                    color: "#3A1E0C",
                    textShadow: "0 1px 0 rgba(255,255,255,0.4)",
                  }}
                  className="text-2xl font-medium leading-tight"
                >
                  First match of the day!
                </p>
                <p className="text-base font-semibold mt-0.5" style={{ color: "#5C2F0F" }}>
                  +{coins} coins
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
